/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ApolloError } from "@apollo/client";
import { initApolloClient } from "lib/withApollo";
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next";
import { PromotionContextProvider } from "src/contexts/promotion.context";
import { GET_PRODUCT } from "src/hooks/useProductBySlug";
import { ProductView } from "src/organisms/ProductView";
import { SingleProduct } from "src/types/graphql.d";
import { formatName } from "src/utils/formatName";

type ProductPageProps = {
  product?: SingleProduct;
  error: ApolloError | string | null;
  loading: boolean;
  slug: string;
};

const ProductPage = ({
  pageProps: { product, error, loading = true, slug },
}: {
  pageProps: ProductPageProps;
}): JSX.Element => {
  return (
    <PromotionContextProvider>
      <ProductView
        product={product}
        productError={error}
        productLoading={loading}
        slug={slug}
      />
    </PromotionContextProvider>
  );
};

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps(
  context: GetStaticPropsContext<{
    productSlug: string;
  }>
): Promise<GetStaticPropsResult<ProductPageProps>> {
  if (!context.params || !context.params.productSlug) {
    return {
      notFound: true,
    };
  }

  const slug = context.params?.productSlug;

  const apolloClient = initApolloClient({
    locale: process.env.LOCALE,
    storeKey: process.env.STORE_KEY,
  });

  try {
    const { data } = await apolloClient.query({
      query: GET_PRODUCT,
      variables: { slug },
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const product: undefined | SingleProduct = data?.productBySlug && {
      ...data.productBySlug,
      name: formatName((data.productBySlug.name as string) || ""),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      brand: {
        ...data.productBySlug.brand,
        name: formatName((data?.productBySlug.brand?.name as string) || ""),
      },
    };

    if (!product) {
      return { notFound: true };
    }

    return {
      revalidate: 60 * 15,
      props: {
        product,
        loading: false,
        error: null,
        slug,
      },
    };
  } catch (error: unknown) {
    if (error instanceof ApolloError) {
      const message = error?.graphQLErrors?.[0]?.message;
      const isNotFound =
        message === "No product is found with the provided slug" ||
        (error?.graphQLErrors?.[0]?.extensions?.code === "NOT_FOUND" &&
          error?.graphQLErrors?.[0]?.path?.[0] === "productBySlug");

      if (isNotFound) {
        return { notFound: true };
      }

      return {
        props: {
          error,
          loading: false,
          slug,
        },
      };
    }

    return {
      props: {
        loading: false,
        error: "An unknown error occurred",
        slug,
      },
    };
  }
}

export default ProductPage;
